import Editor from "@monaco-editor/react";
import { useEffect, useRef, useState } from "react";
import { parseDefinition, serializeDefinition } from "../state/definition";
import type { SimulationDef } from "../types";

interface JsonTabProps {
  definition: SimulationDef;
  onChange: (definition: SimulationDef) => void;
  onParseError: (error: string | null) => void;
  /** External sync is suppressed while the user's text doesn't parse. */
  parseError: string | null;
}

export function JsonTab({ definition, onChange, onParseError, parseError }: JsonTabProps) {
  const [text, setText] = useState(() => serializeDefinition(definition));
  // When the definition changes from outside (form edit, library open), refresh the text;
  // when this editor produced the change, leave the user's formatting alone.
  // Never clobber the user's broken-but-being-edited text.
  const lastEmitted = useRef<SimulationDef>(definition);
  useEffect(() => {
    if (parseError !== null) return; // never clobber the user's broken-but-being-edited text
    if (definition !== lastEmitted.current) {
      setText(serializeDefinition(definition));
      lastEmitted.current = definition;
    }
  }, [definition, parseError]);

  const handleChange = (value: string | undefined) => {
    const next = value ?? "";
    setText(next);
    try {
      const parsed = parseDefinition(next);
      lastEmitted.current = parsed;
      onParseError(null);
      onChange(parsed);
    } catch (error) {
      onParseError(String(error));
    }
  };

  return (
    <Editor
      height="100%"
      defaultLanguage="json"
      theme="vs-dark"
      value={text}
      onChange={handleChange}
      options={{
        minimap: { enabled: false },
        fontSize: 13,
        scrollBeyondLastLine: false,
        wordWrap: "on",
        tabSize: 2,
      }}
    />
  );
}
